import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Calendar, Download, FileText, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminReports() {
  const [reportType, setReportType] = useState("daily");
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7)
  );
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("user");

    if (!token || !userData) {
      navigate("/login/admin");
      return;
    }

    const user = JSON.parse(userData);
    if (user.role !== "admin") {
      navigate("/login/admin");
      return;
    }
  }, [navigate]);

  const fetchReport = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");

    try {
      let url = "";
      if (reportType === "daily") {
        url = `${API}/attendance/reports/daily?date=${selectedDate}`;
      } else {
        url = `${API}/attendance/reports/monthly?month=${selectedMonth}`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setReportData(response.data);
    } catch (error) {
      toast.error("Gagal memuat laporan");
    } finally {
      setLoading(false);
    }
  };

  const getShiftName = (shiftNumber) => {
    const shifts = {
      1: "Shift 1 (09:00-17:00)",
      2: "Shift 2 (17:00-01:00)",
      3: "Shift 3 (01:00-09:00)",
    };
    return shifts[shiftNumber] || shiftNumber;
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-reports-page">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Attendance Reports</h1>
          <p className="text-slate-400">View daily and monthly attendance reports</p>
        </div>

        {/* Filter Section */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Filter Report
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label className="text-slate-200">Report Type</Label>
                <select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-600 text-white"
                  data-testid="report-type-select"
                >
                  <option value="daily">Daily</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              {reportType === "daily" ? (
                <div className="space-y-2">
                  <Label className="text-slate-200">Date</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="pl-10 bg-slate-900 border-slate-600 text-white"
                      data-testid="date-input"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label className="text-slate-200">Month</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      type="month"
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      className="pl-10 bg-slate-900 border-slate-600 text-white"
                      data-testid="month-input"
                    />
                  </div>
                </div>
              )}

              <div className="flex items-end">
                <Button
                  onClick={fetchReport}
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  data-testid="generate-report-button"
                >
                  {loading ? "Loading..." : "Show Report"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Report Results */}
        {reportData && (
          <div className="space-y-6">
            {/* Summary Cards */}
            {reportType === "daily" && (
              <div className="grid gap-4 md:grid-cols-4">
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-slate-300">Total Hadir</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-white">
                      {reportData.total_present}
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-slate-300">Shift 1</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-blue-400">
                      {reportData.shift_1}
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-slate-300">Shift 2</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-purple-400">
                      {reportData.shift_2}
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-slate-300">Shift 3</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-cyan-400">
                      {reportData.shift_3}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {reportType === "monthly" && (
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Monthly Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-white">
                    Total: {reportData.total_records} attendances
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Attendance Table */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">Attendance Details</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
                  data-testid="export-report-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </CardHeader>
              <CardContent>
                <div className="rounded-lg border border-slate-700 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700 hover:bg-slate-800">
                        <TableHead className="text-slate-300">Name</TableHead>
                        <TableHead className="text-slate-300">Email</TableHead>
                        <TableHead className="text-slate-300">Date</TableHead>
                        <TableHead className="text-slate-300">Shift</TableHead>
                        <TableHead className="text-slate-300">Check-in Time</TableHead>
                        <TableHead className="text-slate-300">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(reportType === "daily"
                        ? reportData.records
                        : reportData.all_records
                      ).length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            className="text-center text-slate-400 py-8"
                          >
                            <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>No attendance data</p>
                          </TableCell>
                        </TableRow>
                      ) : (
                        (reportType === "daily"
                          ? reportData.records
                          : reportData.all_records
                        ).map((record, index) => (
                          <TableRow
                            key={index}
                            className="border-slate-700 hover:bg-slate-750"
                            data-testid={`attendance-row-${index}`}
                          >
                            <TableCell className="font-medium text-white">
                              {record.employee_name}
                            </TableCell>
                            <TableCell className="text-slate-300">
                              {record.employee_email}
                            </TableCell>
                            <TableCell className="text-slate-300 font-mono">
                              {record.date}
                            </TableCell>
                            <TableCell>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-900 text-blue-200">
                                {getShiftName(record.shift)}
                              </span>
                            </TableCell>
                            <TableCell className="text-slate-300 font-mono">
                              {new Date(record.check_in_time).toLocaleTimeString(
                                "id-ID"
                              )}
                            </TableCell>
                            <TableCell>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-900 text-green-200">
                                {record.status}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}